import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Button, FloatingInput, TagSelect } from '@piplapps/pipl-ui';
import ApiService from '../../../Services/ApiService';
import LoadingButton from '../../../Components/LoadingButton/index';
import { UsersActions } from '../../../Redux/modules/users/actions';
import { UsersSelectors } from '../../../Redux/modules/users/selectors';
import styles from './AddUserModal.module.less';
import { extractError } from '../../../Utils/ErrorUtils';
import PermissionsToggles from './ActionModals/PermissionsToggles';
import { getText } from '../../../Utils/TranslationUtils';
import AnalyticsEventNames from '../../../Utils/AnalyticsHandler/AnalyticsEventNames';
import AnalyticsHandler from '../../../Utils/AnalyticsHandler/AnalyticsHandler';
import { AppSelectors } from '../../../Redux/modules/app/selectors';

function isEmailValid(email) {
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
}

const AddUserModal = (props) => {
  const [isInviteMode, setIsInviteMode] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailErr, setEmailError] = useState(null);
  const [nameError, setNameError] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamsNames, setTeamsNames] = useState([]);

  const validate = async (values) => {
    const errors = {};
    if (values.email === '') {
      errors.email = gettext('This field is required');
    }

    if (values.email && !isEmailValid(values.email)) {
      errors.email = gettext('Invalid email address');
    }
    if (emailErr) {
      errors.email = getText(emailErr);
    }
    if (nameError && values.firstName === '') {
      errors.firstName = getText(nameError);
    }
    if (nameError && values.lastName === '') {
      errors.lastName = getText(nameError);
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      isAdmin: false,
      isPro: false,
    },
    validate,
    onSubmit: (values) => {
      if (!isInviteMode) {
        props.onCreateUser({
          ...values,
          teams,
        });
      } else {
        props.onInviteUser({
          email: values.email,
          isAdmin: values.isAdmin,
          isPro: values.isPro,
          teams,
        });
      }
    },
  });

  async function checkEmail(email) {
    setIsCheckingEmail(true);
    try {
      const resp = await ApiService.validateEmail({ email });
      const { isExist, isInviteable, isInvited } = resp;

      if (isExist && !isInviteable) {
        const errorText = gettext(
          'A user with that email has already an active subscription and therefore can not be invited.',
        );
        setEmailError(errorText);
        setNameError('This field is required');
        formik.setFieldError('email', errorText);
      }

      if (isInvited) {
        const errorText = gettext('This user already has a pending invitation to join your organization');
        setEmailError(errorText);
        setNameError('This field is required');
        formik.setFieldError('email', errorText);
      }

      if (isInviteable) {
        setIsInviteMode(true);
        setIsCheckingEmail(false);
        setEmailError(null);
        formik.setFieldError('email', null);
        setNameError(null);
        return {};
      }
      if (!isExist) {
        formik.setFieldError('email', null);
        setEmailError(null);
        setNameError('This field is required');
      }
      setIsInviteMode(false);
    } catch (e) {
      const emailError = extractError(e?.response?.data);

      if (emailError && email) {
        setEmailError(emailError);
        setNameError('This field is required');
        formik.setFieldError('email', emailError);
      }
    }

    setIsCheckingEmail(false);
    return true;
  }

  const teamOptions = props.teams?.map((team) => {
    return { label: team.name, value: team.id.toString() };
  });

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <div className={styles.header} id="modal-title">
          {gettext('Invite user')}
        </div>
        <div className={styles.modalContainer}>
          <span
            tabIndex={0}
            role="button"
            className={styles.close}
            onClick={() => {
              props.clickedCloseModalButton();
            }}
          >
            &times;
          </span>
          <div className={styles.inputWrapper}>
            <FloatingInput
              type="email"
              name="email"
              id="add-user-email"
              className={styles.textInput}
              touched={formik.touched}
              onBlur={async (e) => {
                formik.setFieldTouched('email', true);
                formik.setFieldValue('email', e.target.value);
                await checkEmail(formik.values.email?.trim());
              }}
              errors={formik.errors}
              onChange={formik.handleChange}
              value={formik.values.email}
              placeholder={gettext('Email')}
              isLoading={isCheckingEmail}
              autoComplete="off"
              size="large"
            />
          </div>
          <>
            <div className={styles.inputWrapper}>
              <FloatingInput
                disabled={isInviteMode}
                className={styles.textInput}
                type="text"
                name="firstName"
                id="add-user-first-name"
                touched={formik.touched}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                onChange={formik.handleChange}
                value={isInviteMode ? '' : formik.values.firstName}
                placeholder={gettext('First Name')}
              />
            </div>
            <div className={styles.inputWrapper}>
              <FloatingInput
                disabled={isInviteMode}
                className={styles.textInput}
                type="text"
                name="lastName"
                id="add-user-last-name"
                touched={formik.touched}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                onChange={formik.handleChange}
                value={isInviteMode ? '' : formik.values.lastName}
                placeholder={gettext('Last Name')}
              />
            </div>
            {teamOptions?.length > 0 && props.isTeamManagement ? (
              <div className={styles.inputWrapper}>
                <TagSelect
                  options={teamOptions}
                  name="teamsSelect"
                  id="teamsSelect"
                  testId="teamsSelect"
                  onChange={(value) => {
                    setTeams(value.map((team) => parseInt(team.value, 10)));
                    setTeamsNames(value.map((team) => team.label));
                  }}
                  label={gettext('Assign to teams')}
                  isSearchable={false}
                  emptyChoicesText="No team is assigned"
                  tagsCaption="Teams"
                  tagsCaptionClassName={styles.tagsCaption}
                  closeMenuOnSelect
                />
              </div>
            ) : null}
            {isInviteMode ? (
              <div className={styles.userExist} id="email-error">
                {gettext(
                  'A user is already registered with this email address, an invitation to join your organization will be sent.',
                )}
              </div>
            ) : null}
          </>
          <strong className={styles.boldTitle}>{gettext('Define permissions')}</strong>
          <div className={styles.togglesWrapper}>
            <PermissionsToggles
              handleSearchToggle={() => formik.setFieldValue('isPro', !formik.values.isPro)}
              handleAdminToggle={() => formik.setFieldValue('isAdmin', !formik.values.isAdmin)}
              searchPermissionStatus={formik.values.isPro}
              adminPermissionStatus={formik.values.isAdmin}
              isTrial={props.isTrial}
              availableSeats={props.availableSubscriptions}
              isSelfUser={false}
            />
          </div>

          <div className={styles.buttonContainer}>
            <div className={styles.buttonWrapper}>
              <AnalyticsHandler
                eventName={AnalyticsEventNames.INVITE_USER_TO_ORG}
                eventData={{
                  invitedUserFirstName: formik.values.firstName,
                  invitedUserLastName: formik.values.lastName,
                  invitedUserEmail: formik.values.email,
                  addToTeams: teamsNames,
                  is_existing_user: isInviteMode,
                  is_admin: formik.values.isAdmin,
                  has_subscription: formik.values.isPro,
                }}
              >
                <LoadingButton
                  buttonClass="primary-blue"
                  id="submit-button"
                  type="submit"
                  loadingText={gettext('Inviting')}
                  text={gettext('Invite')}
                />
              </AnalyticsHandler>
            </div>
            <div className={styles.buttonWrapper}>
              <Button
                buttonClass="secondary-blue"
                id="cancel-button"
                onClick={() => {
                  props.clickedCloseModalButton();
                }}
                text={gettext('Cancel')}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

AddUserModal.propTypes = {
  clickedCloseModalButton: PropTypes.func,
  onCreateUser: PropTypes.func,
  addUserErrorMessage: PropTypes.string,
  onInviteUser: PropTypes.func,
  availableSubscriptions: PropTypes.number,
  isTrial: PropTypes.bool,
  trialEndDate: PropTypes.string,
  teams: PropTypes.array,
  isTeamManagement: PropTypes.bool,
};

const mapStateToProps = (state) => {
  return {
    users: UsersSelectors.orgUsersSelector(state),
    addUserError: UsersSelectors.modalErrorSelector(state),
    addUserErrorMessage: UsersSelectors.modalErrorMessageSelector(state),
    teams: UsersSelectors.teamsSelector(state),
    isTeamManagement: AppSelectors.isTeamManagementSelector(state),
  };
};

const mapDispatchToProps = {
  onCreateUser: UsersActions.createUser,
  onInviteUser: UsersActions.inviteUser,
  clickedCloseModalButton: UsersActions.clickedCloseModalButton,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddUserModal);
